<script setup>
/* eslint-disable vue/no-v-html */
import { inject, computed, onMounted, ref } from 'vue';
import SddsIconCustom from './SddsIconCustom.vue';
import { useRouter, useRoute } from 'vue-router';
import { useMainStore } from '../store/index.js';
import { getDefaultImageLinksByOfferId } from '../services/digitalSalesService.js';
import PriceSuffix from './PriceSuffix';
import Tooltip from './Tooltip.vue';
import lightning_bolt from '@/assets/lightning_bolt.svg';

const adobeEvent = inject('adobeEvent');

const store = useMainStore();
const router = useRouter();
const route = useRoute();

const props = defineProps(
  {
    title: {
      type: String,
      default: '',
    },
    title2: {
      type: String,
      default: '',
    },
    offerId: {
      type: String,
      default: '',
    },
    keypoints: {
      type: Array,
      default () {
        return [];
      },
    },
    imageUrl: {
      type: String,
      default: '',
    },
    altImage: {
      type: String,
      default: '',
    },
    offerStatus: {
      type: Object,
      default() {
        return {};
      },
    },
    offerData: {
      type: Object,
      default() {
        return {};
      },
    },
    cardHeaderChips: {
      type: Object,
      default() {
        return {};
      },
    },
    cardChips: {
      type: Object,
      default() {
        return {};
      },
    },
    cardBlobs: {
      type: Object,
      default() {
        return {};
      },
    },
    cardBackground: {
      type: String,
      default: '',
    },
    additionalInfo: {
      type: String,
      default: '',
    },
    specialOfferBannerHeader: {
      type: String,
      default: '',
    },
    specialOfferBannerDescription: {
      type: String,
      default: '',
    },
    vehicleCardImageKey: {
      type: String,
      default: '',
    },
    initialPrice: {
      type: Object,
      default() {
        return {};
      },
    },
    offerCategory: {
      type: String,
      default: '',
    },
    serviceSavings: {
      type: Array,
      default() {
        return [];
      },
    },
  },
);

const imageLink = ref(props.imageUrl);

onMounted(async () => {
  // console.log('alt: ', props.altImage);
  if (!store.vehicleCardImage[props.offerId]) {
    const res = await getDefaultImageLinksByOfferId(store.market, props.offerId, { alt: props.altImage, webp: store.webpSupported }).catch(err => {
      console.warn('image link error: ', err);
    });
    store.vehicleCardImage[props.offerId] = res?.data;
  }
  const images = store.vehicleCardImage[props.offerId];
  if (props.vehicleCardImageKey && images && images[props.vehicleCardImageKey]) imageLink.value = images[props.vehicleCardImageKey];
  else if (images?.view) imageLink.value = images.view;
});

const showSpecialOfferBanner = computed(() => {
  return props.specialOfferBannerHeader && props.specialOfferBannerDescription;
});

const offerShouldBeDisabled = computed(() => {
  return (props.offerStatus.expired || props.offerStatus.finalized || props.offerStatus.isOfferNotValid);
});

const showCardHeader = computed(() => {
  return (Object.keys(props.cardChips).length || (props.cardHeaderChips.specialOffer || props.cardHeaderChips.ecolution)) || offerShouldBeDisabled.value;
});

const showCardBlobs = computed(() => {
  return (Object.keys(props.cardBlobs).length) && !offerShouldBeDisabled.value;
});

const showPotentialSavingsChip = computed(() => {
  return props.serviceSavings.length;
});

const potentialSavingsTooltipId = computed(() => {
  return `vehicle-card-potential-savings-${String(props.offerId).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
});

const sendTracking = () => {
  adobeEvent({
    event: 'setupOffer',
    offerInfo: {
      offerId: props.offerId,
      offerCategory: props.offerCategory,
    },
  });
};

const goToConfigure = () => {
  sendTracking();
  router.push({ name: 'PublicConfiguratorView', params: { market: store.market }, query: { id: props.offerId, ref: route.query.ref } });
};
</script>
<template>
  <div class="card-container">
    <template v-if="showCardHeader">
      <div
        class="card-header"
        :class="{ 'card-header-disabled': offerShouldBeDisabled, [`card-header__${store.market}`]: true }"
        data-dd-action-name="build and buy"
        @click="!offerShouldBeDisabled && goToConfigure()"
      >
        <template v-if="offerStatus.expired">
          <div
            class="sdds-chip sdds-chip__icon-right card-header-chip-with-icon"
            style="background-color: var(--sdds-grey-400)"
          >
            <SddsIconCustom name="clock" />
            <span class="sdds-chip-text">{{ $i18n('availableOffersOfferExpired') }}</span>
          </div>
        </template>
        <template v-if="offerStatus.isOfferNotValid">
          <div
            class="sdds-chip sdds-chip__icon-right card-header-chip-with-icon"
            style="background-color: var(--sdds-grey-400)"
          >
            <SddsIconCustom />
            <span class="sdds-chip-text">{{ $i18n('availableOffersSoldOut') }}</span>
          </div>
        </template>
        <template v-if="!offerStatus.expired && !offerStatus.isOfferNotValid && !offerStatus.finalized">
          <template v-if="Object.keys(cardChips).length">
            <div
              v-for="(cardChipCss, cardChipTranslationKey) in cardChips"
              :key="cardChipTranslationKey"
              class="sdds-chip card-header-chip"
              :style="(() => {
                const returnStyleObjct = {};
                if (cardChipCss) {
                  const [backgroundColor, fontColor, fontWeight] = cardChipCss?.split(',');
                  if (backgroundColor) returnStyleObjct.backgroundColor = backgroundColor;
                  if (fontColor) returnStyleObjct.color = fontColor;
                  if (fontWeight) returnStyleObjct.fontWeight = fontWeight;
                }
                return returnStyleObjct;
              })()"
            >
              <span class="sdds-chip-text">{{ $i18n(cardChipTranslationKey) }}
                <template v-if="String(cardChipTranslationKey).toLowerCase().includes('electrictruck')">
                  <img :src="lightning_bolt">
                </template>
              </span>
            </div>
          </template>
          <template v-else>
            <template v-if="cardHeaderChips.specialOffer">
              <div class="sdds-chip card-header-chip chip-special-offer">
                <span class="sdds-chip-text">{{ $i18n('availableOffersSpecialOffer') }}</span>
              </div>
            </template>
            <template v-if="cardHeaderChips.ecolution">
              <div class="sdds-chip card-header-chip chip-ecolution">
                <span class="sdds-chip-text">{{ $i18n('availableOffersEcolution') }}</span>
              </div>
            </template>
          </template>
        </template>
      </div>
    </template>
    <div
      class="card-top"
      :class="{ 'card-top-disabled': offerShouldBeDisabled }"
      :style="{ ...(cardBackground ? { 'background-image': `linear-gradient(${cardBackground}, var(--sdds-grey-50) 70%)` } : {}) }"
      data-dd-action-name="build and buy"
      @click="!offerShouldBeDisabled && goToConfigure()"
    >
      <div class="card-img-container">
        <img
          :src="imageLink"
          :class="{'card-img card-img-disabled': offerShouldBeDisabled, 'card-img': !offerShouldBeDisabled }"
          alt="Card Image"
        >
        <div
          v-if="showCardBlobs"
          class="card-blobs-container"
        >
          <div
            v-for="(cardBlobColors, cardBlobTranslationKey) in cardBlobs"
            :key="cardBlobTranslationKey"
            class="card-blob"
            :style="(() => {
              const returnStyleObjct = {};
              if (cardBlobColors?.split(',')[0]) returnStyleObjct.backgroundColor = cardBlobColors.split(',')[0];
              if (cardBlobColors?.split(',')[1]) returnStyleObjct.color = cardBlobColors.split(',')[1];
              return returnStyleObjct;
            })()"
          >
            <!-- <span class="card-blob-text">{{ $i18n('eMobBlobUpToText') }}</span> -->
            <!-- <span class="card-blob-text-value">{{ $i18n(cardBlobTranslationKey) }}</span> -->
            <span
              class="card-blob-text-value"
              v-html="$i18n(cardBlobTranslationKey)"
            />
          </div>
        </div>
        <div
          :class="{'card-img-banner': showSpecialOfferBanner, 'card-img-banner-disabled': !showSpecialOfferBanner }"
        >
          <div class="card-img-banner-header">
            {{ $i18n(specialOfferBannerHeader) }}
          </div>
          <div class="card-img-banner-description">
            {{ $i18n(specialOfferBannerDescription) }}
          </div>
        </div>
      </div>
    </div>
    <div
      class="card"
      :class="{ 'card-disabled': offerShouldBeDisabled, 'sdds-u-pt0': showCardHeader }"
      data-dd-action-name="build and buy"
      @click="!offerShouldBeDisabled && goToConfigure()"
    >
      <div class="card-body">
        <p class="sdds-headline-03">
          <span style="display: block;">
            {{ $i18n(title) }}
          </span>
          <span style="display: block;">
            {{ $i18n(title2) }}&nbsp;
          </span>
        </p>
        <ul class="card-keypoints">
          <li
            v-for="keypoint in keypoints"
            :key="keypoint.icon"
          >
            <div class="sdds-row">
              <div class="sdds-col keypoint-icon sdds-u-relative">
                <SddsIconCustom :name="keypoint.icon" />
              </div>
              <div class="sdds-col keypoint-info">
                {{ $i18n(keypoint.translationKey) }}
              </div>
            </div>
          </li>
        </ul>
        <div
          class="sdds-chip potential-savings-chip"
          :class="{ 'potential-savings-chip--placeholder': !showPotentialSavingsChip }"
          @click.stop
        >
          <span class="sdds-chip-text">{{ $i18n('potentialServiceSavings') }}</span>
          <Tooltip
            :selector-id="potentialSavingsTooltipId"
            :text="$i18n('potentialServiceSavingsInformation')"
            :icon-style="{ color: '#0B1320', width: '3.0rem' }"
          />
        </div>
        <template v-if="offerShouldBeDisabled">
          <div
            class="price-configure-btn-container"
            :class="{ 'initial-price-available': initialPrice?.price }"
          >
            <div
              v-if="initialPrice?.price"
              class="initial-price-container"
            >
              <div
                class="initial-price-suffix-container"
                :class="`initial-price-suffix-container__${store.market}`"
              >
                <span class="initial-price-amount">{{ initialPrice.price.toLocaleString('en').replace(/,/g,' ') }}</span>
                <span
                  v-if="initialPrice?.priceSuffix"
                  class="initial-price-suffix"
                >
                  &nbsp;<PriceSuffix
                    :price-suffix="initialPrice.priceSuffix"
                    :inline-block="true"
                  />
                </span>
              </div>
            </div>
            <div
              class="configure-btn-container"
              :class="{ 'configure-btn-container-width': initialPrice?.price }"
            >
              <button
                class="sdds-btn sdds-btn-primary sdds-btn-fullbleed"
                :class="`sdds-btn-primary__${store.market}`"
                disabled
                data-dd-action-name="build and buy card"
              >
                {{ $i18n('buildAndBuyButton') }} <svg
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 32 32"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M17.958 26.314a1 1 0 1 0 1.415 1.414l9.89-9.89a2.6 2.6 0 0 0 0-3.676l-9.89-9.89a1 1 0 1 0-1.415 1.414L27.273 15H2.976a1 1 0 1 0 0 2h24.296l-9.314 9.314Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div
            v-if="offerStatus.expired"
            class="sdds-u-pt1"
          >
            {{ $i18n('offerExpired') }}
            <template v-if="offerData.offerValidDate && offerData.offerValidDate.length && offerData.offerValidDate[0]">
              ({{ offerData.offerValidDate[0] }})
            </template>
          </div>
          <div
            v-if="offerStatus.finalized"
            class="sdds-u-pt1"
          >
            {{ $i18n('confirmationViewHeading') }}
          </div>
          <div
            v-if="offerStatus.isOfferNotValid"
            class="sdds-u-pt1"
          >
            {{ $i18n('offerNotValid') }}
          </div>
        </template>
        <template v-else>
          <div
            class="price-configure-btn-container"
            :class="{ 'initial-price-available': initialPrice?.price }"
          >
            <div
              v-if="initialPrice?.price"
              class="initial-price-container"
            >
              <div
                class="initial-price-suffix-container"
                :class="`initial-price-suffix-container__${store.market}`"
              >
                <span class="initial-price-amount">{{ initialPrice.price.toLocaleString('en').replace(/,/g,' ') }}</span>
                <span
                  v-if="initialPrice?.priceSuffix"
                  class="initial-price-suffix"
                >
                  &nbsp;<PriceSuffix
                    :price-suffix="initialPrice.priceSuffix"
                    :inline-block="true"
                  />
                </span>
              </div>
            </div>
            <div
              class="configure-btn-container"
              :class="{ 'configure-btn-container-width': initialPrice?.price }"
            >
              <a
                class="sdds-btn sdds-btn-primary sdds-btn-fullbleed"
                :class="`sdds-btn-primary__${store.market}`"
                data-dd-action-name="build and buy"
                @click="!offerShouldBeDisabled && goToConfigure()"
              >
                {{ $i18n('buildAndBuyButton') }} <svg
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 32 32"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M17.958 26.314a1 1 0 1 0 1.415 1.414l9.89-9.89a2.6 2.6 0 0 0 0-3.676l-9.89-9.89a1 1 0 1 0-1.415 1.414L27.273 15H2.976a1 1 0 1 0 0 2h24.296l-9.314 9.314Z"
                    fill="currentColor"
                  />
                </svg>
              </a>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div
      v-if="props.additionalInfo"
      :class="{'card-accordion card-accordion-disabled': offerShouldBeDisabled, 'card-accordion': !offerShouldBeDisabled }"
    >
      <sdds-accordion v-if="props.additionalInfo !== null">
        <sdds-accordion-item
          :header="$i18n('moreInformationText')"
          :disabled="offerShouldBeDisabled"
        >
          <div v-html="$i18n(props.additionalInfo)" />
        </sdds-accordion-item>
      </sdds-accordion>
    </div>
  </div>
</template>

<style scoped>

.sdds-btn-fullbleed {
  width: fit-content;
}

.card-container {
  display: flex;
  flex-direction: column;
  margin-top: var(--sdds-spacing-layout-32);
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
  border-radius: 10px 10px 10px 10px;
  overflow: hidden;
  background-image: linear-gradient(#A9A9A9, var(--sdds-grey-50), var(--sdds-grey-50));
}
.card-header {
  position: absolute;
  padding: var(--sdds-spacing-element-16);
  padding-bottom: var(--sdds-spacing-element-8);
  z-index: 1;
  width: calc(33% - var(--sdds-spacing-layout-64));
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
}
.card-header__pl {
  width: calc(25% - var(--sdds-spacing-layout-64));
}

.card-header__de {
  width: calc(25% - var(--sdds-spacing-layout-64));
}
.card-header__ch {
  width: calc(25% - var(--sdds-spacing-layout-64));
}
.card-header__jp {
  width: calc(25% - var(--sdds-spacing-layout-64));
}

.card-header.card-header-disabled {
  cursor: not-allowed;
}
.card-header .sdds-chip {
  margin: 0;
  cursor: default;
}
.card-header .card-header-chip-with-icon .sdds-icon-custom {
  height: 16px;
  width: 16px;
  margin-right: 0;
  position: absolute;
  right: 8px;
}
.card-body .tick-icon.sdds-icon-custom {
  height: 24px;
  width: 24px;
  position: absolute;
  top: 1rem;
  left: 0.4rem;
}
.card-body .clock-icon.sdds-icon-custom {
  height: 20px;
  width: 20px;
  margin-top: 1rem;
  margin-left: 0.4rem;
}
.card-header .card-header-chip-with-icon object {
  height: 16px;
  width: 16px;
  margin-right: 0;
  position: absolute;
  right: 8px;
}
.card-header .card-header-chip.chip-special-offer {
  background-color: #DA2B40;
}
.card-header .card-header-chip.chip-ecolution {
  background-color: #D95610;
}
.card-header .card-header-chip {
  color: #fff;
}
.sdds-chip-text img {
  vertical-align: middle;
}

.card-top {
  padding-top: var(--sdds-spacing-element-40);
  display: flex;
  flex-direction: column;
  background-image: linear-gradient(#A9A9A9, var(--sdds-grey-50), var(--sdds-grey-50));
  cursor: pointer;
}
.card-top-disabled {
  cursor: not-allowed;
}

.card {
  display: flex;
  flex-direction: column;
  background-color: var(--sdds-grey-50);
  cursor: pointer;
}

.card-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.card-img-container {
  position: relative;

  width: 100%;
  height: 100%;
}
.card-img-container::after {
  display: block;
  content: '';
  /* 4:3 aspect ratio */
  padding-bottom: 75%;
}
.card-img-container .card-img {
  /* Image should match parent box size */
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}
.card-img-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.card-img-container .card-img-banner {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: auto;
  padding: 2.5rem 5.0rem;
  background-color: #DA2B40;
}
.card-img-container .card-img-banner-disabled {
  display: none;
}
.card-img-banner .card-img-banner-header {
  font-family: "Scania Sans Headline", arial, helvetica, sans-serif;
  font-weight: bold;
  font-size: 4.0rem;
  word-wrap: break-word;
  color: #FFFFFF;
}
.card-img-banner .card-img-banner-description {
  font-family: "Scania Sans Condensed", "Scania Sans Semi Condensed", Arial, Helvetica, sans-serif;
  font-weight: normal;
  font-size: 3.0rem;
  word-wrap: break-word;
  color: #FFFFFF;
}

.card-body {
  display: flex;
  flex-direction: column;
  padding: var(--sdds-spacing-element-20) var(--sdds-spacing-element-20);
  background-color: var(--sdds-grey-50);
}

.card-accordion{
  margin-top: auto;
  background-color: var(--sdds-grey-50);
}
.card-accordion-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.configure-btn-container>button,
.configure-btn-container>a {
  display: flex;
  place-content: center;
  align-items: center;
  margin-top: auto;
  color: white;
}

.configure-btn-container>button>svg,
.configure-btn-container>a>svg {
  line-height: 0;
  margin-left: var(--sdds-spacing-element-12);
  height: 6rem;
  width: 6rem;
}

.card-keypoints {
  list-style: none;
  margin: auto 0;
  padding: 0;
  /* margin-bottom: var(--sdds-spacing-layout-16); */
  line-height: 1.4em;
}

.card-keypoints li {
  margin-bottom: var(--sdds-spacing-layout-8);
}

.sdds-headline-03 {
  margin: unset;
  margin-bottom: var(--sdds-spacing-element-12);
}

.card-keypoints li .keypoint-icon {
  max-width: fit-content;
  padding: 0;
  margin-top: -1rem;
}

.card-keypoints li .keypoint-info {
  padding: 0 0 0 4px;
}

.card-additional-info{
  margin-top: var(--sdds-spacing-element-4);
}

.potential-savings-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--sdds-spacing-element-8);
  width: fit-content;
  margin-bottom: var(--sdds-spacing-element-12);
  padding: 5px 10px;
  background-color: #9BD9C2;
  color: #0B1320;
  border-radius: 4px;
}
.potential-savings-chip--placeholder {
  opacity: 0;
  pointer-events: none;
}
.potential-savings-chip .sdds-chip-text {
  color: inherit;
  font-weight: 500;
}

.card-blobs-container {
  position: absolute;
  top: 20px;
  right: 80px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.card-blob {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #fff;
  font-size: 3rem;
  font-weight: bold;
  height: 110px;
  width: 110px;
  border-radius: 50%;
}

.card-blob-text {
  font-weight: normal;
  margin-bottom: 0.5rem;
}

.card-blob-text-value {
  font-size: 6rem;
  line-height: 0.8;
}

.price-configure-btn-container {
  display: flex;
  justify-content: space-between;
  padding: var(--sdds-spacing-element-12) 0;
}
.price-configure-btn-container.initial-price-available {
  background-color: rgba(148, 134, 119, 0.15);
  padding: var(--sdds-spacing-element-12);
  border-radius: 4px;
}

.initial-price-container {
  display: flex;
  align-items: center;
}

.initial-price-suffix-container {
  display: flex;
  align-items: baseline;
}

.initial-price-amount {
  font-family: 'Scania Sans Headline';
  font-size: xxx-large;
  font-weight: bold;
}

.initial-price-suffix {
  font-size: large;
}

@media only screen and (max-width: 2100px) {
  .configure-btn-container-width {
    max-width: 250px;
  }
}

@media only screen and (max-width: 2130px) {
  .initial-price-amount {
    font-size: xx-large;
  }
  .initial-price-suffix {
    font-size: medium;
  }
  .initial-price-suffix-container__pl {
    flex-direction: column;
  }

  .initial-price-suffix-container__de {
    flex-direction: column;
  }
  .initial-price-suffix-container__ch {
    flex-direction: column;
  }
  .initial-price-suffix-container__jp {
    flex-direction: column;
  }
}

@media only screen and (max-width: 1900px) {
  .configure-btn-container-width {
    max-width: 200px;
  }
}

@media only screen and (max-width: 1600px) {
  .initial-price-suffix-container {
    flex-direction: column;
  }
  .initial-price-amount {
    font-size: x-large;
  }
  .initial-price-suffix {
    font-size: small;
  }
  .sdds-btn-primary__pl {
    padding-left: 8px;
    padding-right: 8px;
  }
  
  .sdds-btn-primary__de {
    padding-left: 8px;
    padding-right: 8px;
  }
  .sdds-btn-primary__ch {
    padding-left: 8px;
    padding-right: 8px;
  }
  .sdds-btn-primary__jp {
    padding-left: 8px;
    padding-right: 8px;
  }
}

@media only screen and (max-width: 1366px) {
  .initial-price-suffix-container {
    flex-direction: row;
  }
  .initial-price-amount {
    font-size: xxx-large;
  }
  .initial-price-suffix {
    font-size: large;
  }
}

@media only screen and (max-width: 1366px) {
  .card-header {
    width: calc(47% - var(--sdds-spacing-layout-48));
  }
}

@media only screen and (max-width: 1200px) {
  .initial-price-suffix-container {
    flex-direction: column;
  }
  .initial-price-amount {
    font-size: xx-large;
  }
  .initial-price-suffix {
    font-size: medium;
  }
}

@media only screen and (max-width: 1024px) {
  .active-offers__card-list>* {
    width: 100%;
    margin: unset;
  }
}

@media only screen and (max-width: 900px) {
  .initial-price-amount {
    font-size: x-large;
  }
  .initial-price-suffix {
    font-size: small;
  }
  .configure-btn-container-width {
    max-width: 180px;
  }
}

@media only screen and (max-width: 800px) {
  .configure-btn-container-width {
    max-width: 160px;
  }
}

@media only screen and (max-width: 768px) {
  .card {
    flex-direction: column;
  }
  .card-body {
    margin-top: var(--sdds-spacing-element-16);
    width: 100%;
  }
  .card-header {
    width: calc(100% - var(--sdds-spacing-layout-32));
  }
  .initial-price-suffix-container {
    flex-direction: row;
  }
  .initial-price-amount {
    font-size: xx-large;
  }
  .initial-price-suffix {
    font-size: medium;
  }
  .configure-btn-container-width {
    max-width: unset;
  }
}

@media only screen and (max-width: 460px) {
  .initial-price-suffix-container {
    flex-direction: column;
  }
  .initial-price-amount {
    font-size: x-large;
  }
  .initial-price-suffix {
    font-size: small;
  }
}

@media only screen and (max-width: 600px) {
  .configure-btn-container-width {
    max-width: 180px;
  }
}

@media only screen and (max-width: 350px) {
  .initial-price-amount {
    font-size: large;
  }
  .initial-price-suffix {
    font-size: x-small;
  }
  .configure-btn-container-width {
    max-width: 160px;
  }
}
</style>
